import React from "react";
import { Star } from "lucide-react";

const toAlignClass = (a) => {
  const v = String(a || "").toLowerCase().trim();
  if (v === "center") return "text-center";
  if (v === "right") return "text-right";
  if (v === "left") return "text-left";
  return "";
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 180;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const TestimonialsSection1 = ({ config, style }) => {
  const renderStars = (rating = 5) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        style={{
          color:
            i < rating
              ? style?.testimonialsStarActiveColor || undefined
              : style?.testimonialsStarInactiveColor || undefined,
          fill:
            i < rating
              ? style?.testimonialsStarActiveColor || undefined
              : "transparent",
        }}
        className={`w-5 h-5 ${
          i < rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
        }`}
      />
    ));
  };

  const bg = buildBg(style, "bg-white");

  const align = (style?.textAlign || "").trim();
  const hasAlign = align.length > 0;

  const headerAlignClass = hasAlign ? toAlignClass(align) : "text-center";
  const cardAlignClass = hasAlign ? toAlignClass(align) : "";

  const testimonials = Array.isArray(config?.testimonials) ? config.testimonials : [];

  return (
    <section
      dir={config?.direction || "ltr"}
      style={bg.style}
      className={`py-20 px-4 ${bg.className}`}
    >
      <div className="container mx-auto max-w-7xl">
        <h2
          style={{ color: style?.titleColor || undefined }}
          className={`text-3xl md:text-4xl font-bold text-gray-900 mb-16 ${headerAlignClass}`}
        >
          {config?.title}
        </h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              style={{
                backgroundColor: style?.testimonialsCardBg || undefined,
                borderColor: style?.testimonialsCardBorder || undefined,
              }}
              className={`bg-gray-50 p-8 rounded-3xl border border-gray-200 hover:border-blue-300 transition-all duration-300 ${cardAlignClass}`}
            >
              <div className="flex items-center mb-6">
                <div
                  style={{
                    backgroundColor: style?.testimonialsAvatarBg || undefined,
                    color: style?.testimonialsAvatarText || undefined,
                  }}
                  className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold text-xl me-4"
                >
                  {testimonial.avatar}
                </div>
                <div>
                  <h4
                    style={{ color: style?.testimonialsNameColor || undefined }}
                    className="font-bold text-gray-900"
                  >
                    {testimonial.name}
                  </h4>
                  <p
                    style={{ color: style?.testimonialsRoleColor || undefined }}
                    className="text-gray-600 text-sm"
                  >
                    {testimonial.role}
                  </p>
                </div>
              </div>

              <div className="flex mb-4">{renderStars(testimonial.rating)}</div>

              <p
                style={{ color: style?.testimonialsContentColor || style?.subtitleColor || undefined }}
                className="text-gray-700 text-lg italic"
              >
                "{testimonial.content}"
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection1;